import { useState, useEffect } from 'react';
import { useToast } from '../components/Toast';
import { getAllMembers, searchAllMembers, notifyMember } from '../lib/data';
import { getSession, onAuthStateChange } from '../lib/auth';
import { apiClient } from '../lib/api/client';
import { segmentDirectoryEntries } from '../lib/members';
import MemberCard from '../components/MemberCard';
import MemberModal from '../components/MemberModal';
import NotifyMembersModal from '../components/NotifyMembersModal';
import InviteGuestModal from '../components/InviteGuestModal';

import { createGuestInvite, listGuestInvites } from '../lib/guestInvites';

function normalizeProspect(prospect) {
  if (!prospect || typeof prospect !== 'object') return null;
  return {
    ...prospect,
    id: prospect.id ?? prospect.prospect_id ?? prospect.prospectId ?? prospect.email,
    name: prospect.name ?? prospect.fullName ?? prospect.full_name ?? 'Unnamed Prospect',
    businessName: prospect.businessName ?? prospect.business_name ?? prospect.business ?? '',
    currentStage: prospect.currentStage ?? prospect.current_stage ?? 'Unknown stage',
    email: prospect.email ?? '',
    phone: prospect.phone ?? '',
    createdAt: prospect.createdAt ?? prospect.created_at ?? null,
  };
}

function mergeProspects(primary, secondary) {
  const byId = new Map();
  [...(primary ?? []), ...(secondary ?? [])]
    .map(normalizeProspect)
    .filter(Boolean)
    .forEach((prospect) => {
      byId.set(String(prospect.id), {
        ...(byId.get(String(prospect.id)) ?? {}),
        ...prospect,
      });
    });
  return Array.from(byId.values());
}

function roleFromSession(session) {
  const rawRole = session?.user?.role ?? session?.user?.uiRole ?? session?.user?.backendRole ?? null;
  return typeof rawRole === 'string' ? rawRole.toLowerCase() : null;
}

export default function Members() {
  const { showToast } = useToast();
  const [directory, setDirectory] = useState({ members: [], subs: [], prospects: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [prospectsPanelStatus, setProspectsPanelStatus] = useState('idle');
  const [guestInvites, setGuestInvites] = useState([]);
  const [guestInviteMeeting, setGuestInviteMeeting] = useState(null);
  const [isInviteGuestModalOpen, setIsInviteGuestModalOpen] = useState(false);

  useEffect(() => {
    loadDirectory();

    const checkUserRole = async () => {
      const session = await getSession();
      setUserRole(roleFromSession(session));
    };

    const {
      data: { subscription },
    } = onAuthStateChange((_event, session) => {
      setUserRole(roleFromSession(session));
    });

    const loadGuestInvites = async () => {
      try {
        const resp = await listGuestInvites();
        setGuestInviteMeeting(resp?.meeting ?? null);
        setGuestInvites(Array.isArray(resp?.invites) ? resp.invites : []);
      } catch (error) {
        console.error('Error loading guest invites:', error);
        setGuestInviteMeeting(null);
        setGuestInvites([]);
      }
    };

    checkUserRole();
    loadGuestInvites();

    return () => subscription.unsubscribe();
  }, []);

  const loadProspects = async () => {
    try {
      const response = await apiClient.get('/api/prospects', {
        params: { offset: 0, limit: 200 },
      });
      setProspectsPanelStatus('loaded');
      return Array.isArray(response?.prospects) ? response.prospects : [];
    } catch (error) {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        setProspectsPanelStatus('unavailable');
        return [];
      }
      setProspectsPanelStatus('error');
      throw error;
    }
  };

  const loadDirectory = async () => {
    try {
      const [memberResponse, prospectResponse] = await Promise.all([
        getAllMembers({ limit: 100 }),
        loadProspects(),
      ]);
      const segmented = segmentDirectoryEntries(memberResponse.members);
      setDirectory({
        members: segmented.members,
        subs: segmented.subs,
        prospects: mergeProspects(segmented.prospects, prospectResponse),
      });
    } catch (err) {
      const msg = 'Failed to load members';
      showToast(msg, { kind: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      loadDirectory();
      return;
    }

    setIsLoading(true);
    try {
      const response = await searchAllMembers(searchQuery, { limit: 100 });
      const segmented = segmentDirectoryEntries(response.members);
      setDirectory((prev) => ({
        members: segmented.members,
        subs: segmented.subs,
        prospects: prev.prospects,
      }));
    } catch (err) {
      const msg = 'Failed to search members';
      showToast(msg, { kind: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotifyAll = async ({ message }) => {
    try {
      // In a real app, we'd send one notification to all members
      // Here we simulate by notifying each member individually using their preferred method
      await Promise.all(directory.members.map(member =>
        notifyMember(member.id, member.notificationPreference || 'email', message)
      ));
      // Show success message (in a real app, you'd want to use a toast notification)
      alert('Notification sent successfully');
    } catch (err) {
      const msg = 'Failed to send notifications';
      showToast(msg, { kind: 'error' });
    }
  };

  const handleOpenModal = (member) => {
    // Conservative: until role is known, do not allow opening member details.
    if (!userRole || userRole === 'prospect') return;
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const handleInviteGuest = async (payload) => {
    try {
      await createGuestInvite(payload);
      showToast('Guest invited for the next meeting.', { kind: 'success' });
      const resp = await listGuestInvites();
      setGuestInviteMeeting(resp?.meeting ?? null);
      setGuestInvites(Array.isArray(resp?.invites) ? resp.invites : []);
    } catch (error) {
      console.error('[members] invite guest failed:', error);
      showToast(error?.message ?? 'Failed to invite guest.', { kind: 'error' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-semibold text-[#0E4069] mb-6">Members</h1>
      <div className="mb-6 flex flex-wrap gap-3 text-sm text-gray-600">
        <span className="rounded-full bg-white px-3 py-1 shadow-sm">Members: {directory.members.length}</span>
        <span className="rounded-full bg-white px-3 py-1 shadow-sm">Subs: {directory.subs.length}</span>
        <span className="rounded-full bg-white px-3 py-1 shadow-sm">Prospects: {directory.prospects.length}</span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex-grow max-w-2xl">
          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="input-field flex-grow"
            />
            <button type="submit" className="btn-primary">
              Search
            </button>
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  loadDirectory();
                }}
                className="btn-secondary"
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Notify All Button - Hidden for prospects */}
        {userRole && userRole !== 'prospect' && (
          <button
            onClick={() => setIsNotifyModalOpen(true)}
            className="btn-primary whitespace-nowrap"
          >
            Notify All Members
          </button>
        )}
      </div>

      {/* Error Message */}
      

      {/* Members Grid */}
      <section className="mb-12">
        <h2 className="mb-4 text-2xl font-semibold text-[#0E4069]">Members</h2>
        {directory.members.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {directory.members.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onOpenModal={handleOpenModal}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No active members found.</p>
        )}
      </section>

      {directory.subs.length > 0 ? (
        <section className="mb-12">
          <h2 className="mb-4 text-2xl font-semibold text-[#0E4069]">Subs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {directory.subs.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onOpenModal={handleOpenModal}
              />
            ))}
          </div>
        </section>
      ) : null}

      {userRole && userRole !== 'prospect' ? (
        <section className="mb-12">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-2xl font-semibold text-[#0E4069]">Prospects</h2>
            {prospectsPanelStatus === 'unavailable' ? (
              <span className="text-sm text-gray-500">Live prospects panel not available for this account/backend.</span>
            ) : null}
          </div>

          {directory.prospects.length > 0 ? (
            <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {directory.prospects.map((prospect) => (
                <li key={prospect.id} className="rounded-lg border bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900">{prospect.name}</div>
                      <div className="text-sm text-gray-600">{prospect.businessName || 'Business not provided'}</div>
                      {prospect.email ? <div className="text-sm text-gray-600">{prospect.email}</div> : null}
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {prospect.currentStage}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">
              {prospectsPanelStatus === 'loaded'
                ? 'No prospects available.'
                : 'Prospect data is not currently available from the backend.'}
            </p>
          )}
        </section>
      ) : null}

      {/* Invited Guests (Next Meeting) - Hidden for prospects */}
      {userRole && userRole !== 'prospect' ? (
        <section>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-[#0E4069]">Invited Guests</h2>
              {guestInviteMeeting?.startsAt ? (
                <p className="mt-1 text-sm text-gray-600">
                  Next meeting: {new Date(guestInviteMeeting.startsAt).toLocaleString()}
                </p>
              ) : null}
            </div>
            <button
              onClick={() => setIsInviteGuestModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-NBNA-primary hover:bg-NBNA-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-NBNA-primary"
            >
              Invite Guest
            </button>
          </div>

          {guestInvites.length > 0 ? (
            <ul className="space-y-2">
              {guestInvites.map((inv) => (
                <li key={inv.id} className="rounded-lg border bg-white p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <div className="font-medium text-gray-900">{inv?.prospectMember?.name || 'Guest'}</div>
                      <div className="text-sm text-gray-600">{inv?.prospectMember?.businessName || ''}</div>
                      <div className="text-sm text-gray-600">{inv?.prospectMember?.email || ''}</div>
                    </div>
                    <div className="text-sm text-gray-700">Status: {inv.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No invited guests yet.</p>
          )}
        </section>
      ) : null}

      {/* Member Modal */}
      <MemberModal
        member={selectedMember}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMember(null);
        }}
      />

      {/* Notify Members Modal */}
      <NotifyMembersModal
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        onSubmit={handleNotifyAll}
      />

      {/* Invite Guest Modal */}
      <InviteGuestModal
        isOpen={isInviteGuestModalOpen}
        onClose={() => setIsInviteGuestModalOpen(false)}
        onSubmit={handleInviteGuest}
      />
    </div>
  );
}
